import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { licenseDetailTab } from "@/lib/license-detail-tab";
import { adminCreateLicensePath, adminLicensePath } from "@/lib/navigation";
import { canRetainLicenseView, expiryWindow, licenseScope, licenseView, licenseViewFilters, licenseViewStatus, withLicenseScope } from "@/lib/license-views";
import { LicenseViewToolbar } from "@/components/admin/license-view-toolbar";
import { LicenseScopeSelector } from "@/components/admin/license-scope-selector";
import { can } from "@/lib/permissions";
import {
  queryKeys,
  useCurrentIdentityQuery,
  useLicenseIndexQuery,
} from "@/lib/queries";
import type {
  LicenseListItem,
  SearchResult,
} from "@/lib/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  EmptyState,
  InlineAlert,
  LoadingRows,
} from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import {
  SearchResultsTable,
  type SearchRow,
} from "@/components/admin/search-results-table";

const LICENSES_PATH = "/admin/licenses";

export function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentView = licenseView(searchParams.get("view"));
  const scope = licenseScope(searchParams);
  const viewFilters = { ...licenseViewFilters(searchParams), ...scope };
  const viewReady = currentView !== "expiring" || Boolean(viewFilters.expires_from);
  const query = searchParams.get("q") ?? "";
  // Default status is active only for the bare/default view. Inbound URLs of
  // other views without an explicit status (old bookmarks) keep the preset
  // semantics, matching what the view toolbar writes.
  const requestedStatus = licenseViewStatus(currentView, searchParams.get("status") ?? (currentView === "all" ? "active" : "all"));
  const selectedLicenseID = searchParams.get("license") || "";
  const createRequested = searchParams.get("create") === "1";
  const [input, setInput] = useState(query);
  const [statusFilter, setStatusFilter] = useState(requestedStatus);
  const [bookmarkReset, setBookmarkReset] = useState(false);
  const identityQuery = useCurrentIdentityQuery();
  const roles = identityQuery.data?.roles;
  const submittedStatus = requestedStatus;
  const listQuery = query.trim();
  const licenseIndexQuery = useLicenseIndexQuery(
    listQuery,
    submittedStatus,
    viewReady,
    viewFilters,
  );
  const activeQuery = licenseIndexQuery;
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    const rawStatus = searchParams.get("status");
    if (rawStatus && rawStatus !== requestedStatus) {
      next.set("status", requestedStatus);
      changed = true;
    }
    if (!viewReady) {
      // A malformed expiry bookmark still carries raw params; a fresh preset click does not.
      const hadExpiryBookmark = searchParams.get("expires_from") !== null || searchParams.get("expires_before") !== null;
      Object.entries(expiryWindow()).forEach(([key, value]) => next.set(key, value));
      changed = true;
      if (hadExpiryBookmark) setBookmarkReset(true);
    }
    if (!changed) return;
    void navigate({ search: next.toString() }, { replace: true });
  }, [navigate, requestedStatus, searchParams, viewReady]);
  useEffect(() => {
    if (!createRequested) return;
    const next = new URLSearchParams();
    const tour = searchParams.get("tour");
    if (tour) next.set("tour", tour);
    const search = next.toString();
    navigate(search ? `${adminCreateLicensePath()}?${search}` : adminCreateLicensePath(), { replace: true });
  }, [createRequested, navigate, searchParams]);
  useEffect(() => {
    if (!selectedLicenseID) return;
    const next = new URLSearchParams(searchParams);
    next.delete("license");
    next.delete("create");
    const focus = next.get("focus") ?? "";
    const tab = licenseDetailTab(next.get("tab"), focus);
    const detail = new URLSearchParams();
    if (focus) {
      detail.set("focus", focus);
      next.delete("focus");
    }
    if (tab !== "overview") {
      detail.set("tab", tab);
      next.delete("tab");
    } else {
      next.delete("tab");
    }
    const indexSearch = next.toString();
    navigate(
      {
        pathname: adminLicensePath(selectedLicenseID),
        search: detail.toString(),
      },
      {
        replace: true,
        state: {
          ...(location.state && typeof location.state === "object" ? location.state : {}),
          indexReturnTo: indexSearch ? `${LICENSES_PATH}?${indexSearch}` : LICENSES_PATH,
        },
      },
    );
  }, [location.state, navigate, searchParams, selectedLicenseID]);
  useEffect(() => { if (currentView !== "expiring") setBookmarkReset(false); }, [currentView]);
  const results = useMemo(
    () => (licenseIndexQuery.data?.pages ?? []).flatMap((page) => page.licenses.map(licenseListResult)),
    [licenseIndexQuery.data?.pages],
  );
  useEffect(() => {
    setInput(query);
    setStatusFilter(submittedStatus);
  }, [query, submittedStatus]);

  const filteredRows = useMemo(() => buildRows(results), [results]);

  function setScope(nextScope: { product?: string; customer?: string }) {
    const next = new URLSearchParams(searchParams);
    next.delete("product");
    next.delete("customer");
    next.delete("license");
    next.delete("focus");
    next.delete("tab");
    if (nextScope.product) next.set("product", nextScope.product);
    if (nextScope.customer) next.set("customer", nextScope.customer);
    navigate({ pathname: LICENSES_PATH, search: next.toString() });
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBookmarkReset(false);
    const next = new URLSearchParams();
    const nextView = canRetainLicenseView(currentView, statusFilter) ? currentView : "all";
    const nextViewFilters = nextView === currentView ? viewFilters : scope;
    withLicenseScope(next, searchParams);
    if (nextView !== "all") {
      next.set("view", nextView);
      for (const key of ["expires_from", "expires_before"]) {
        const value = searchParams.get(key);
        if (nextView === "expiring" && value) next.set(key, value);
      }
    }
    if (input.trim()) next.set("q", input.trim());
    next.set("status", statusFilter);
    const nextIndexSearch = input.trim();
    const nextIndexStatus = statusFilter === "all" ? "" : statusFilter;
    const currentIndexStatus = submittedStatus === "all" ? "" : submittedStatus;
    if (nextIndexSearch !== listQuery || nextIndexStatus !== currentIndexStatus) {
      queryClient.removeQueries({ queryKey: queryKeys.licenseIndex(nextIndexSearch, nextIndexStatus, nextViewFilters), exact: true });
    }
    navigate({ pathname: LICENSES_PATH, search: next.toString() });
  }

  function selectRow(row: SearchRow) {
    navigate(adminLicensePath(row.result.license.id), {
      state: { indexReturnTo: `${LICENSES_PATH}${location.search}` },
    });
  }

  const filtersUnsubmitted =
    input.trim() !== query.trim() || statusFilter !== submittedStatus;
  const hasDedicatedFilters =
    Boolean(listQuery) || submittedStatus !== "all" || currentView !== "all" || Boolean(scope.product_id || scope.customer_id);
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError } = licenseIndexQuery;

  function loadMore() {
    void licenseIndexQuery.fetchNextPage({ cancelRefetch: false });
  }

  function refreshResults() {
    const indexStatus = submittedStatus === "all" ? "" : submittedStatus;
    void queryClient.resetQueries({ queryKey: queryKeys.licenseIndex(listQuery, indexStatus, viewFilters), exact: true });
  }

  const title = t("nav.licenses", "Licenses");
  const inputLabel = t(
    "search.licenseInputLabel",
    "Search by License ID, Product ID, Customer, or Product Key suffix",
  );

  return (
    <PageFrame size="fluid">
      <PageHeader
        title={title}
        titleClassName="text-balance"
        actions={
          <>
            <Button
              variant="outline"
              onClick={refreshResults}
              disabled={activeQuery.isFetching}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {activeQuery.isFetching
                ? t("common.refreshing", "Refreshing…")
                : t("common.refresh", "Refresh")}
            </Button>
            {can(roles, "createRecords") && (
              <Button
                variant="primary"
                onClick={() => navigate(adminCreateLicensePath())}
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("licenses.createTitle", "New license")}
              </Button>
            )}
          </>
        }
      />

      <div className="flex shrink-0 flex-col gap-2">
        <form
          className="flex flex-wrap items-center gap-2"
          role="search"
          aria-label={t("search.formAria", "Search admin records")}
          onSubmit={submitSearch}
        >
          <label className="relative block min-w-56 flex-1">
            <span className="sr-only">{inputLabel}</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              id="admin-search-input"
              className="pl-9"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t(
                "search.licensePlaceholder",
                "License ID, Product ID, Customer, Product Key suffix…",
              )}
              maxLength={256}
            />
          </label>
          <Select
            aria-label={t("search.statusFilterAria", "Filter by status")}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="w-44 pointer-coarse:h-11"
          >
            <option value="all">{t("search.allStatuses", "All statuses")}</option>
            <option value="active">{t("common.active", "Active")}</option>
            <option value="suspended">{t("common.suspended", "Suspended")}</option>
            <option value="revoked">{t("common.revoked", "Revoked")}</option>
            <option value="expired">{t("common.expired", "Expired")}</option>
            <option value="cancelled">{t("common.cancelled", "Cancelled")}</option>
          </Select>
          <Button
            type="submit"
            variant="outline"
          >
            <Search className="size-4" aria-hidden="true" />
            {t("search.filter", "Find records")}
          </Button>
          {filtersUnsubmitted && (
            <p className="w-full text-xs text-warning" role="status">
              {t("search.unsubmittedFilters", "Filters changed. Submit to update results.")}
            </p>
          )}
        </form>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <LicenseScopeSelector scope={scope} onChange={setScope} />
          <LicenseViewToolbar view={currentView} onSelect={(params) => {
            setBookmarkReset(false);
            navigate({ pathname: LICENSES_PATH, search: withLicenseScope(params, searchParams).toString() });
          }} />
        </div>
        {currentView === "expiring" && bookmarkReset && viewFilters.expires_from && <p role="status" className="shrink-0 text-xs text-warning">{t("uxViews.bookmarkReset", { from: viewFilters.expires_from, before: viewFilters.expires_before })}</p>}
        {currentView === "expiring" && viewFilters.expires_from && <p className="shrink-0 text-xs text-muted">{t("uxViews.range", { from: viewFilters.expires_from, before: viewFilters.expires_before })}</p>}
        {currentView === "at_capacity" && <p className="shrink-0 text-xs text-muted">{t("uxViews.capacityDefinition")}</p>}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <section
            className="flex min-h-0 flex-1 flex-col rounded-lg border border-line bg-panel overflow-hidden"
            aria-labelledby="results-heading"
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
              <div>
                <h2 id="results-heading" className="font-semibold text-ink">
                  {t("search.results", "Results")}
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  {listQuery
                    ? t("search.matchesFor", "Matches for “{{query}}”", { query: listQuery })
                    : t("search.newestRecords", "Newest records first")}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-3 text-xs text-muted">
                {results.length > 0 && (
                  <span className="hidden md:inline">{t("search.keyboardHint", "↑↓ moves between rows")}</span>
                )}
                <span>
                  {results.reduce(
                    (sum, result) =>
                      sum + result.active_activation_count,
                    0,
                  )} {t("search.activeDevices", "active devices")}
                </span>
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              {activeQuery.isError && results.length === 0 ? (
                <div className="p-4">
                  <InlineAlert
                    title={t("search.listLoadError", "Records could not load")}
                    message={t("search.loadErrorDescription", "Try again after checking the Admin API connection.")}
                    onRetry={refreshResults}
                  />
                </div>
              ) : activeQuery.isFetching && results.length === 0 ? (
                <div className="p-4"><LoadingRows /></div>
              ) : filteredRows.length === 0 && !hasDedicatedFilters ? (
                <EmptyState
                  title={t("search.licenseIndexEmpty", "No Licenses yet")}
                  message={t("search.licenseIndexEmptyDesc", "New Licenses will appear here after they are issued.")}
                />
              ) : filteredRows.length === 0 ? (
                <EmptyState
                  title={t("search.noResults", "No matching records")}
                  message={t("search.noResultsDesc", "Try a customer name, License ID, key suffix, or installation ID.")}
                />
              ) : (
                <SearchResultsTable
                  rows={filteredRows}
                  onSelect={selectRow}
                />
              )}
            </div>
            {(hasNextPage || isFetchNextPageError) && (
              <div className="flex shrink-0 justify-center border-t border-line px-4 py-3">
                {isFetchNextPageError ? (
                  <div className="w-full">
                    <InlineAlert
                      title={t("search.loadMoreError", "More records could not load")}
                      message={t("search.loadMoreErrorDesc", "Loaded records remain available. Retry the next page when ready.")}
                      onRetry={loadMore}
                    />
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isFetchingNextPage}
                    onClick={loadMore}
                  >
                    {isFetchingNextPage
                      ? t("search.loadingMore", "Loading more…")
                      : t("search.loadMore", "Load more")}
                  </Button>
                )}
              </div>
            )}
          </section>
      </div>
    </PageFrame>
  );
}

type LicenseListSource = LicenseListItem &
  Partial<
    Pick<
      SearchResult,
      | "activations"
      | "activations_truncated"
      | "key_suffixes"
      | "key_suffixes_truncated"
    >
  >;

function licenseListResult(item: LicenseListSource): SearchResult {
  const activations = item.activations ?? [];
  return {
    product: item.product,
    customer: item.customer,
    license: item.license,
    activation_count: item.activation_count,
    active_activation_count: item.active_activation_count,
    activations,
    activations_truncated:
      item.activations_truncated ??
      item.activation_count > activations.length,
    key_suffixes: item.key_suffixes ?? [],
    key_suffixes_truncated: item.key_suffixes_truncated ?? false,
  };
}

function buildRows(results: SearchResult[]): SearchRow[] {
  return results.map((result) => ({
    id: result.license.id,
    type: "license" as const,
    result,
  }));
}
